import React, { useState, useEffect } from 'react';
import {
  X, Users, ShieldCheck, ChevronLeft, ChevronRight,
  BedDouble, Tag, Star, CheckCircle2, Percent, Wallet
} from 'lucide-react';
import { Button } from './ui/button';

const RoomDetailsModal = ({ isOpen, onClose, room, onBookRoom }) => {
  const [currentImg, setCurrentImg] = useState(0);

  useEffect(() => {
    if (isOpen) setCurrentImg(0);
  }, [isOpen, room]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen || !room) return null;

  const images = room.images?.length > 0 ? room.images : (room.image ? [room.image] : []);
  const hasMultiple = images.length > 1;

  const prev = (e) => { e.stopPropagation(); setCurrentImg(i => (i === 0 ? images.length - 1 : i - 1)); };
  const next = (e) => { e.stopPropagation(); setCurrentImg(i => (i === images.length - 1 ? 0 : i + 1)); };

  const handleBookRoom = () => { onClose(); onBookRoom(room); };

  const amenities = (room.amenities || []).map(a => (typeof a === 'string' ? { name: a } : a));
  const features = room.features || [];
  const rules = room.rules || [];

  const bedLabel = room.beds?.length > 0
    ? room.beds.map(b => `${b.count} ${b.bedName}`).join(' + ')
    : room.numberOfBeds && room.bedType
      ? `${room.numberOfBeds} ${room.bedType} Bed${room.numberOfBeds !== 1 ? 's' : ''}`
      : null;

  const taxAmount = room.price ? (room.price * (room.taxPercent || 0)) / 100 : 0;
  const serviceAmount = room.price ? (room.price * (room.serviceChargePercent || 0)) / 100 : 0;
  const totalPerNight = (room.price || 0) + taxAmount + serviceAmount;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-xs sm:max-w-lg md:max-w-2xl lg:max-w-4xl max-h-[95vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Image carousel */}
        <div className="relative h-56 sm:h-72 lg:h-80 bg-gray-200 flex-shrink-0 overflow-hidden">
          {images.length > 0 ? (
            <img
              src={images[currentImg]}
              alt={`${room.name} ${currentImg + 1}`}
              className="w-full h-full object-cover transition-opacity duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">No image available</div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />

          {hasMultiple && (
            <>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/30 hover:bg-black/60 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all">
                <ChevronLeft size={18} />
              </button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/30 hover:bg-black/60 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all">
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-14 left-0 right-0 flex justify-center gap-1.5">
                {images.map((_, i) => (
                  <button key={i} onClick={e => { e.stopPropagation(); setCurrentImg(i); }} className={`w-2 h-2 rounded-full transition-all ${i === currentImg ? 'bg-white scale-125' : 'bg-white/50'}`} />
                ))}
              </div>
              <div className="absolute top-4 right-14 bg-black/40 text-white text-xs px-2 py-1 rounded-full">
                {currentImg + 1} / {images.length}
              </div>
            </>
          )}

          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 bg-black/20 hover:bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all">
            <X size={18} />
          </button>

          {/* Name + badges overlay */}
          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white drop-shadow mb-1.5">{room.name}</h2>
            <div className="flex flex-wrap items-center gap-2">
              {room.roomClassName && (
                <span className="bg-blue-600/90 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">{room.roomClassName}</span>
              )}
              {room.available !== undefined && (
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${room.available ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'}`}>
                  {room.available ? `${room.availableUnits} Available` : 'Not Available'}
                </span>
              )}
            </div>
          </div>

          {/* Price badge */}
          <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1.5 rounded-lg shadow-lg">
            <div className="text-lg font-bold">৳{room.price?.toLocaleString()}</div>
            <div className="text-xs opacity-90">per night</div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left: details */}
            <div className="lg:col-span-2 space-y-5">

              {/* Key stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {bedLabel && (
                  <div className="flex items-center gap-2.5 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <BedDouble size={18} className="text-blue-600 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">Beds</div>
                      <div className="text-sm font-semibold text-gray-800">{bedLabel}</div>
                    </div>
                  </div>
                )}
                {room.occupancy > 0 && (
                  <div className="flex items-center gap-2.5 p-3 bg-green-50 rounded-lg border border-green-100">
                    <Users size={18} className="text-green-600 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">Max Guests</div>
                      <div className="text-sm font-semibold text-gray-800">{room.occupancy}</div>
                    </div>
                  </div>
                )}
                {room.taxPercent > 0 && (
                  <div className="flex items-center gap-2.5 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <Percent size={18} className="text-amber-600 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">Tax</div>
                      <div className="text-sm font-semibold text-gray-800">{room.taxPercent}%</div>
                    </div>
                  </div>
                )}
                {room.serviceChargePercent > 0 && (
                  <div className="flex items-center gap-2.5 p-3 bg-rose-50 rounded-lg border border-rose-100">
                    <Wallet size={18} className="text-rose-600 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">Service Charge</div>
                      <div className="text-sm font-semibold text-gray-800">{room.serviceChargePercent}%</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              {room.description && (
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">About This Room</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{room.description}</p>
                </div>
              )}

              {/* Features */}
              {features.length > 0 && (
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                    <Tag size={15} className="text-blue-600" /> Room Features
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {features.map((f, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-100">{f}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Amenities */}
              {amenities.length > 0 && (
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                    <Star size={15} className="text-amber-500" /> Amenities
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {amenities.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-sm">
                        <CheckCircle2 size={14} className="text-blue-600 flex-shrink-0" />
                        <span className="font-medium text-gray-700">{a.name ?? a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rules */}
              {rules.length > 0 && (
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                    <ShieldCheck size={15} className="text-green-600" /> Room Rules & Policies
                  </h3>
                  <ul className="space-y-1.5">
                    {rules.map((rule, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right: booking panel */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200 sticky top-0">
                <h3 className="text-base font-semibold mb-4">Book This Room</h3>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Base Rate</span>
                    <span className="font-semibold">৳{room.price?.toLocaleString()}</span>
                  </div>
                  {taxAmount > 0 && (
                    <div className="flex justify-between items-center text-gray-500">
                      <span>Tax ({room.taxPercent}%)</span>
                      <span>৳{taxAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  )}
                  {serviceAmount > 0 && (
                    <div className="flex justify-between items-center text-gray-500">
                      <span>Service ({room.serviceChargePercent}%)</span>
                      <span>৳{serviceAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-2 flex justify-between items-center font-semibold">
                    <span>Total / Night</span>
                    <span className="text-blue-600 text-lg">৳{totalPerNight.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                </div>
                {room.availableUnits !== undefined && (
                  <p className={`text-xs text-center mb-3 font-medium ${room.availableUnits > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {room.availableUnits > 0 ? `${room.availableUnits} of ${room.totalQuantity} units available` : 'Fully booked'}
                  </p>
                )}
                <Button
                  onClick={handleBookRoom}
                  disabled={room.available === false}
                  className="w-full"
                  variant={room.available === false ? 'secondary' : 'default'}
                >
                  {room.available === false ? 'Not Available' : 'Book This Room'}
                </Button>
                <p className="text-xs text-gray-400 text-center mt-2">No charge until confirmation</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetailsModal;
